import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import { type Table } from '@tanstack/react-table';
import {
  selectIsZoomInDisabled,
  selectIsZoomOutDisabled,
  useUserSettingsStore,
} from '@/hooks/data/settings/useUserSettingsStore';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { type PageSizes } from '@/types/pagination';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  className?: string;
  refine?: (value: number) => void;
  grouped?: boolean;
  pageSizeOptions: PageSizes;
}

export function DataTablePagination<TData>({
  table,
  className,
  refine,
  grouped = false,
  pageSizeOptions,
}: DataTablePaginationProps<TData>) {
  /**
   * Disabled state comes from the store's derived selectors, matching the View → Zoom menu items
   * exactly and only re-rendering these buttons when the disabled boolean actually flips, not on
   * every intermediate zoom step.
   */
  const isZoomOutDisabled = useUserSettingsStore(selectIsZoomOutDisabled);
  const isZoomInDisabled = useUserSettingsStore(selectIsZoomInDisabled);
  const zoomIn = useUserSettingsStore((s) => s.zoomIn);
  const zoomOut = useUserSettingsStore((s) => s.zoomOut);
  const { isManagePage } = useCurrentPage();

  const t = useTranslations('pagination');
  const groupedCount = table.getGroupedRowModel().rows.length;
  const filteredCount = table.getFilteredRowModel().rows.length;
  const totalCount = table.getRowCount();
  const currentPage = table.getPageCount() === 0 ? 1 : table.getState().pagination.pageIndex + 1;
  const pageCount = table.getPageCount() === 0 ? 1 : table.getPageCount();
  const pageSize = table.getState().pagination.pageSize;
  // A persisted page size from another context (e.g. search-only 250) is a valid store value, so show it
  // as an extra option rather than a fallback: the control must always agree with what the table fetched.
  const pageSizes = pageSizeOptions.includes(pageSize)
    ? pageSizeOptions
    : [...pageSizeOptions, pageSize].sort((a, b) => a - b);
  const tMenu = useTranslations('menu');

  return (
    <div className={cn('flex h-12 max-h-12 min-h-12 items-center justify-between bg-ui-base px-2', className)}>
      <div className="flex w-full items-center gap-x-2 @[800px]/main:gap-x-10">
        <div className="line-clamp-2 grow text-sm text-balance text-text-gray">
          {grouped
            ? t('showingGroups', { current: groupedCount, total: totalCount })
            : t('showingEntries', { current: filteredCount, total: totalCount })}
        </div>
        <div className="flex items-center gap-x-2">
          <p className="text-sm whitespace-nowrap text-text-base">
            {grouped ? t('groupsPerPage') : t('resultsPerPage')}
          </p>
          <Select
            data-cy="data-table-pagination-page-size-select"
            value={String(pageSize)}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
              if (refine) {
                refine(Number(value));
              }
            }}
          >
            <SelectTrigger className="h-8 w-17.5">
              <SelectValue placeholder={pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {pageSizes.map((size) => (
                <SelectItem key={size} value={`${size}`}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div
          data-cy="data-table-pagination-current-page-text"
          className="flex items-center justify-center text-sm whitespace-nowrap text-text-base"
        >
          {t('pageLabel', {
            current: currentPage,
            total: pageCount,
          })}
        </div>
        <div className="flex items-center gap-x-1 @[800px]/main:gap-x-2">
          <Button
            variant="main-toolbar"
            size="main-toolbar"
            className="hidden size-6 p-0 @[700px]/main:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            data-cy="data-table-pagination-first-page-button"
            tooltip={t('goToFirstPage')}
            icon={ChevronFirst}
          />
          <Button
            variant="main-toolbar"
            size="main-toolbar"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            data-cy="data-table-pagination-previous-page-button"
            tooltip={t('goToPreviousPage')}
            icon={ChevronLeft}
          />
          <Button
            variant="main-toolbar"
            size="main-toolbar"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            data-cy="data-table-pagination-next-page-button"
            tooltip={t('goToNextPage')}
            icon={ChevronRight}
          />
          <Button
            variant="main-toolbar"
            size="main-toolbar"
            className="hidden @[700px]/main:flex"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
            data-cy="data-table-pagination-last-page-button"
            tooltip={t('goToLastPage')}
            icon={ChevronLast}
          />
          {!isManagePage && (
            <>
              <Button
                variant="main-toolbar"
                size="main-toolbar"
                data-cy="toolbar-zoom-out-button"
                tooltip={tMenu('zoom.out')}
                icon={ZoomOut}
                disabled={isZoomOutDisabled}
                onClick={zoomOut}
              />
              <Button
                variant="main-toolbar"
                size="main-toolbar"
                data-cy="toolbar-zoom-in-button"
                tooltip={tMenu('zoom.in')}
                icon={ZoomIn}
                disabled={isZoomInDisabled}
                onClick={zoomIn}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
